'use client';

/**
 * useIsMobile — SSR-safe viewport breakpoint hook.
 *
 * Returns true when the viewport is at or below `maxWidth` pixels.
 * Uses matchMedia and subscribes to changes.
 *
 * Chosen over user-agent / pointer / touch detection because:
 *   - Touch laptops, iPads and ChromeOS blur any UA-based definition of "mobile".
 *   - Viewport width is what actually determines whether our UI fits.
 *   - Our Electron window has a minWidth well above any breakpoint we use here,
 *     so resized desktop windows can't accidentally trigger mobile layouts.
 *
 * Consumers:
 *   - VoidlinePreJoin passes 900 (wider threshold — absolute overlays start to
 *     collide with the centered sign around that width).
 *   - VoidlineRoom passes 700 (tight threshold — bar fits fine above 700, and
 *     the Electron minWidth sits above it so desktop is never affected).
 */

import { useEffect, useState } from 'react';

export function useIsMobile(maxWidth = 700): boolean {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${maxWidth}px)`);
    setMobile(mq.matches);
    const listener = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, [maxWidth]);
  return mobile;
}
